import React, {useCallback, useMemo} from 'react';
import {Divider, Stack} from '@mui/material';
import {useSession} from '../../firebase/UserProvider';
import {useFolderNavigation, useIcrData, useSnippetDrop, useYSourcePackage} from './hooks';
import {buildBreadcrumb} from './utils/sourcePackageHelpers';
import SourcePackageHeader from './SourcePackageHeader';
import SourcePackageSummary from './Summary/SourcePackageSummary';
import FolderItemsList from './FolderItemsList';
import {useReportStore} from '../../features/products/useReportStore';

/**
 * SourcePackage - Main component for managing document source collections.
 * Displays folders and items, supports navigation, drag-drop, and data import.
 */
const SourcePackage = ({embeddedInSidePanel = false}) => {
    const {user} = useSession();

    // Navigation state from URL
    const {path, currentFolderId, isRootLevel, pop, push} = useFolderNavigation();

    // Source package data and actions (from Yjs)
    const {
        folders,
        items,
        addFolder,
        addItem,
        renameFolder,
        removeFolder,
        moveFolder,
        moveItem,
        removeItem,
        isEmpty,
    } = useYSourcePackage();

    // ICR-related data still comes from the report store (not yet in Yjs)
    const reportId = useReportStore(s => s.id || s.reportId);
    const meta = useReportStore(s => s.meta);
    const informationRequirement = useReportStore(s => s.informationRequirement);
    const eei = useReportStore(s => s.eei);
    const backgroundJustification = useReportStore(s => s.backgroundJustification);
    const subject = useReportStore(s => s.subject || s.meta?.title);
    const targetInformation = useReportStore(s => s.targetInformation);
    const icrNumber = meta?.icrNumber;
    const hasEmbeddedIcr = !!(informationRequirement || eei || backgroundJustification);
    const embeddedIcrData = hasEmbeddedIcr
        ? {
            subject: subject || 'Untitled',
            informationRequirement: informationRequirement || '',
            eei: eei || {},
            backgroundJustification: backgroundJustification || '',
            targetInformation: targetInformation || {},
        }
        : null;

    // ICR data for summary
    const {icr, isLoading: icrLoading} = useIcrData({
        hasEmbeddedIcr,
        embeddedIcrData,
        icrNumber,
    });


    // Drag-drop for container
    const snippetDropHandlers = useSnippetDrop({
        addItem,
        moveItem,
        folderId: currentFolderId,
        userId: user?.uid,
    });


    // Derived: breadcrumb
    const breadcrumb = useMemo(
        () => buildBreadcrumb(folders, path),
        [folders, path]
    );

    // Handlers
    const handleAddFolder = useCallback(() => {
        // Filter to siblings in the same parent folder (using reactive Yjs folders)
        const siblings = folders.filter(f =>
            (f.parentFolderId || null) === (currentFolderId || null)
        );

        // Find the next available number
        let maxNum = 0;
        siblings.forEach(f => {
            const match = f.name?.match(/^Folder (\d+)$/);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxNum) maxNum = num;
            }
        });

        // Compute append position: max sibling position + STEP (default 10)
        const maxPosition = siblings.reduce((max, f) => Math.max(max, f.position ?? 0), 0);
        const position = maxPosition + 10;

        addFolder({
            name: `Folder ${maxNum + 1}`,
            parentFolderId: currentFolderId || null,
            position,
            userId: user?.uid,
        });
    }, [addFolder, currentFolderId, folders, user?.uid]);

    // Show summary only at root when ICR exists and source package has content
    const showSummary = isRootLevel && icr && !icrLoading && !isEmpty;

    return (
        <Stack height={embeddedInSidePanel ? undefined : '100%'}>
            <SourcePackageHeader
                breadcrumb={breadcrumb}
                pathLength={path.length}
                onUp={pop}
                onAddFolder={handleAddFolder}
            />

            <Divider/>

            {showSummary && (
                <>
                    <SourcePackageSummary
                        reportId={reportId}
                        icr={icr}
                        userId={user?.uid}
                        items={items}
                    />
                    <Divider/>
                </>
            )}

            <Stack
                flex={embeddedInSidePanel ? undefined : 1}
                flexGrow={embeddedInSidePanel ? undefined : 1}
                overflow={embeddedInSidePanel ? 'visible' : 'auto'}
                p={2}
                bgcolor="#e9ecef"
                {...snippetDropHandlers}
            >
                <FolderItemsList
                    currentParentId={currentFolderId}
                    folders={folders}
                    items={items}
                    onOpenFolder={push}
                    addItem={addItem}
                    userId={user?.uid}
                    renameFolder={renameFolder}
                    removeFolder={removeFolder}
                    moveFolder={moveFolder}
                    moveItem={moveItem}
                    removeItem={removeItem}
                />
            </Stack>

        </Stack>
    );
};

export default SourcePackage;
